"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";

import { isAxiosError } from "axios";

import { api, ensureRefresh } from "@/services";
import { GetSessionsResponse } from "@/services/api";
import { User } from "@/services/types";

type AuthContextType = {
  user?: User;
  sessions?: GetSessionsResponse;
  refreshAuth: () => Promise<boolean>;
  loadAuthState: (options?: LoadAuthStateOptions) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshSessions: () => Promise<void>;
  destroySession: (
    sessionId: string,
    updateSessions?: boolean,
  ) => Promise<void>;
  destroySessions: (updateSessions?: boolean) => Promise<void>;
  setUser: React.Dispatch<React.SetStateAction<User | undefined>>;
  loading: boolean;
};

type LoadAuthStateOptions = {
  setLoading?: boolean;
  clearOnFailure?: boolean;
  requireSessions?: boolean;
  refreshOnFailure?: boolean;
};

type AuthStateRequestConfig = Parameters<typeof api.instance.get>[1] & {
  skipAuthRefresh?: boolean;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User>();
  const [sessions, setSessions] = useState<GetSessionsResponse>();
  const [loading, setLoading] = useState<boolean>(false);
  const refreshPromiseRef = React.useRef<Promise<boolean> | null>(null);

  const loadAuthState = useCallback(async (
    options: LoadAuthStateOptions = {},
  ): Promise<boolean> => {
    const shouldSetLoading = options.setLoading ?? true;
    const shouldClearOnFailure = options.clearOnFailure ?? true;
    const requireSessions = options.requireSessions ?? false;
    const refreshOnFailure = options.refreshOnFailure ?? false;
    if (shouldSetLoading) setLoading(true);
    try {
      const skipAuthRefresh = (): AuthStateRequestConfig => ({
        headers: { "Cache-Control": "no-cache" },
        skipAuthRefresh: true,
      } as AuthStateRequestConfig);
      const loadUser = () => api.instance.get<User>('/user', skipAuthRefresh());
      let userResponse = await loadUser().catch(async (error) => {
        if (!refreshOnFailure) throw error;
        await ensureRefresh();
        return await loadUser();
      });
      setUser(userResponse.data);

      try {
        const sessionsResponse = await api.instance.get<GetSessionsResponse>('/auth/session', skipAuthRefresh());
        setSessions(sessionsResponse.data);
      } catch (err) {
        setSessions(undefined);
        if (requireSessions) throw err;
      }

      return true;
    } catch (err) {
      if (shouldClearOnFailure) {
        setUser(undefined);
        setSessions(undefined);
      }
      return false;
    } finally {
      if (shouldSetLoading) setLoading(false);
    }
  }, []);

  const refreshAuth = useCallback(async (): Promise<boolean> => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    const promise = (async () => {
      setLoading(true);
      try {
        const refreshed = await ensureRefresh()
          .then(() => true)
          .catch((error) => {
            if (isAxiosError(error) && error.response) {
              if (error.response.status === 401) {
                return false;
              }
            }

            return Promise.reject(error);
        });

        if (refreshed) {
          return await loadAuthState({ requireSessions: true });
        }

        setUser(undefined);
        setSessions(undefined);

        return false;
      } catch (err) {
        console.error("Auth exception:", err);
        return false;
      } finally {
        setLoading(false);
        refreshPromiseRef.current = null;
      }
    })();

    refreshPromiseRef.current = promise;
    return promise;
  }, [loadAuthState]);

  const logout = useCallback(async () => {
    try {
      await api.auth.onPostLogoutAuthLogoutPost();
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      setUser(undefined);
      setSessions(undefined);
    }
  }, []);

  // Sessions

  const refreshSessions = useCallback(async () => {
    try {
      setSessions((await api.auth.sessionsAuthSessionGet()).data);
    } catch (err) {
      console.error("Exception on refresh sessions:", err);
    }
  }, []);

  const destroySession = useCallback(
    async (sessionId: string, updateSessions?: boolean) => {
      try {
        if (sessions?.current_session?.session_id === sessionId) {
          await logout();
          return;
        } else {
          await api.auth.invalidateSessionAuthSessionSessionIdDelete(sessionId);
        }

        if (updateSessions) await refreshSessions();
      } catch (err) {
        console.error("Destroy session error:", err);
      }
    },
    [sessions, logout, refreshSessions],
  );

  const destroySessions = useCallback(
    async (updateSessions?: boolean) => {
      try {
        if (!sessions) return;

        const currentId = sessions.current_session?.session_id;
        const promises =
          sessions.other
            ?.filter((s) => s.session_id !== currentId)
            .map((s) =>
              api.auth.invalidateSessionAuthSessionSessionIdDelete(
                s.session_id,
              ),
            ) ?? [];

        if (!promises.length) return;

        await Promise.all(promises);
        if (updateSessions) await refreshSessions();
      } catch (err) {
        console.error("Destroy sessions error:", err);
      }
    },
    [sessions, refreshSessions],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        sessions,
        refreshAuth,
        loadAuthState,
        logout,
        refreshSessions,
        destroySession,
        destroySessions,
        setUser,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");

  return ctx;
}
